/**
 * Toast store (T11-06): one feedback rule for the whole admin — success → toast (+ Undo for 10 s
 * when the action can be reversed), problems → an error toast with the reference code. At most 3
 * toasts are on screen; the rest wait. Framework-free so the QueryClient can report errors too.
 */
import { createContext, useContext, useSyncExternalStore } from "react";

export type ToastKind = "success" | "error" | "info";

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  /** Plain-language "what to do next" under the message. */
  hint?: string;
  /** Reference code (`requestId`) the staff member can copy for the owner / support. */
  requestId?: string | null;
  /** Reverses the action (API call). Only for actions that can be undone. */
  undo?: () => unknown;
  /** Milliseconds until it closes by itself (`null` = stays until closed). */
  ttl: number | null;
  createdAt: number;
}

export interface ToastOptions {
  hint?: string;
  requestId?: string | null;
  undo?: () => unknown;
  ttl?: number | null;
  /** Replace an existing toast with the same key instead of adding another one. */
  dedupeKey?: string;
}

export const MAX_VISIBLE_TOASTS = 3;
export const UNDO_TTL_MS = 10_000;
const SUCCESS_TTL_MS = 5_000;

export interface ToastStore {
  subscribe(listener: () => void): () => void;
  getSnapshot(): readonly Toast[];
  show(kind: ToastKind, message: string, options?: ToastOptions): number;
  success(message: string, options?: ToastOptions): number;
  error(message: string, options?: ToastOptions): number;
  info(message: string, options?: ToastOptions): number;
  dismiss(id: number): void;
  clear(): void;
}

export function createToastStore(now: () => number = Date.now): ToastStore {
  let toasts: readonly Toast[] = [];
  let seq = 0;
  const keys = new Map<string, number>();
  const listeners = new Set<() => void>();
  const emit = () => {
    for (const listener of listeners) {
      listener();
    }
  };
  const store: ToastStore = {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => toasts,
    show(kind, message, options = {}) {
      const ttl =
        options.ttl !== undefined
          ? options.ttl
          : options.undo
            ? UNDO_TTL_MS
            : kind === "error"
              ? null
              : SUCCESS_TTL_MS;
      const previous = options.dedupeKey ? keys.get(options.dedupeKey) : undefined;
      seq += 1;
      const toast: Toast = {
        id: seq,
        kind,
        message,
        ttl,
        createdAt: now(),
        ...(options.hint ? { hint: options.hint } : {}),
        ...(options.requestId ? { requestId: options.requestId } : {}),
        ...(options.undo ? { undo: options.undo } : {}),
      };
      toasts = [...toasts.filter((t) => t.id !== previous), toast];
      if (options.dedupeKey) {
        keys.set(options.dedupeKey, toast.id);
      }
      emit();
      return toast.id;
    },
    success: (message, options) => store.show("success", message, options),
    error: (message, options) => store.show("error", message, options),
    info: (message, options) => store.show("info", message, options),
    dismiss(id) {
      if (toasts.some((t) => t.id === id)) {
        toasts = toasts.filter((t) => t.id !== id);
        emit();
      }
    },
    clear() {
      toasts = [];
      keys.clear();
      emit();
    },
  };
  return store;
}

export const ToastContext = createContext<ToastStore | null>(null);

export function useToast(): ToastStore {
  const store = useContext(ToastContext);
  if (!store) {
    throw new Error("useToast must be used inside <ToastContext>");
  }
  return store;
}

export function useToasts(store: ToastStore): readonly Toast[] {
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}
